import React, { useState } from 'react'

import { useRequest } from 'ahooks'
import clsx from 'clsx'

import ActionButton, {
  ActionButtonSize,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import Toast from '$extensionSrc/components/Toast'
import useForm from '$extensionSrc/hooks/useForm'
import { isValidEmail } from '$extensionSrc/utils/stringValidator'
import FormSubmitButton from '$serverSrc/components/FormSubmitButton'
import FormTextInput from '$serverSrc/components/FormTextInput'
import { authFetch } from '$serverSrc/utils/fetch'

// import './LoginForm.scss'
import { getOauthRequestUrlPath } from '../urlConsts'
import styles from './EnterpriseAdminLoginForm.module.scss'

export default function EnterpriseAdminLoginForm({
  initialError,
}: {
  initialError: string
}) {
  const [urlOptions, setUrlOptions] = useState<string[]>([])
  const [errorMessage, setErrorMessage] = useState<string>(initialError)
  const defaultValues = { email: '' }
  const requiredValidations = {
    email: (x: string) => isValidEmail(x),
  }

  const {
    getFieldChangeHandler,
    getRequiredValidationMetByField,
    isValid,
    values,
  } = useForm({
    defaultValues,
    requiredValidations,
  })

  // hit the /api/get_oauth_request_url endpoint
  const { run: getLoginUrl } = useRequest(
    () => {
      const formData = new FormData()
      formData.append('email', values.email)

      return authFetch(
        new Request(getOauthRequestUrlPath, {
          method: 'POST',
          body: formData,
        }),
      )
    },
    {
      manual: true,
      debounceWait: 300,
      onSuccess: (response: Response) => {
        response.json().then((data) => {
          if (response.status === 200) {
            if (data.login_urls.length === 1) {
              window.location.assign(data.login_urls[0])
            } else if (data.login_urls.length > 1) {
              setUrlOptions(data.login_urls)
            } else {
              setErrorMessage('Invalid email')
              setUrlOptions([])
            }
          } else {
            setErrorMessage('Invalid email')
            setUrlOptions([])
          }
        })
      },
    },
  )

  const submitForm: React.FormEventHandler = (event) => {
    event.preventDefault()
    setUrlOptions([])
    setErrorMessage('')
    getLoginUrl()
  }

  return (
    <form className={styles.EnterpriseAdminLoginForm} onSubmit={submitForm}>
      <div className={clsx(styles.formHeader, 'bold', 'heading1')}>
        <div className={styles.formTitle}>Log into Block Party Enterprise</div>
      </div>
      <div className={styles.formContent}>
        <FormTextInput
          placeholderText="Enter your email"
          name="email"
          isSensitive={false}
          value={values.email}
          handleChange={(value) => getFieldChangeHandler('email')(value)}
          requirements={[
            {
              text: 'Enter a valid email address',
              isFulfilled: getRequiredValidationMetByField('email'),
            },
          ]}
        />
      </div>

      {urlOptions.length === 0 && (
        <FormSubmitButton isValid={isValid}>Continue</FormSubmitButton>
      )}

      {errorMessage && (
        <Toast text={errorMessage} color="red" noAutoDismiss showCloseIcon />
      )}
      {urlOptions.length > 0 &&
        urlOptions.map((url) => (
          <ActionButton
            key={url}
            role="link"
            type={ActionButtonType.PRIMARY}
            size={ActionButtonSize.LARGE}
            disabled={!isValid}
            onClick={() => window.location.assign(url)}
          >
            Continue with {url}
          </ActionButton>
        ))}
    </form>
  )
}
